import React, {useEffect, useState, Fragment} from "react";
import {LoggedInView} from "../common/LoggedInView";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {useLocation, useParams} from "react-router-dom";
import Breadcrumbs from "../common/Breadcrumbs";
import {getServices, resetUpdateServiceStatus, updateEmails} from "../../store/reducers/ServicesSlice";
import {Service} from "../../models/Service";
import {NewServiceFormProps} from "./NewServiceScreen";
import ElogTextarea from "../common/form/ElogTextarea";
import {EmailText} from "../../models/EmailText";
import {RequestStatus} from "../../utils/Enums";
import {FormikProvider, useFormik, Form} from "formik";
import {createVendorContact} from "../../store/reducers/VendorContactsSlice";
import WysiwygEditorField from "../orders/components/WysiwygEditorField";

interface ServiceEmailsScreenProps {
}

export const ServiceEmailsScreen: React.FC<ServiceEmailsScreenProps> = () => {

    const dispatch = useAppDispatch();
    const location = useLocation();

    const servicesState = useAppSelector(state => state.services);

    const requestStatus: RequestStatus = servicesState.updateEmailsStatus;

    const {id} = useParams();

    const [language, setLanguage] = useState<string>('');
    const [service, setService] = useState<Service | null>(null);

    useEffect(() => {
        dispatch(getServices(false));
        if (id) {
            const selectedService = servicesState.services.find(service => service.id === parseInt(id));
            if (selectedService) {
                setService(selectedService);
            }
        }
    }, [id]);

    useEffect(() => {
        dispatch(resetUpdateServiceStatus());
    }, [location]);

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: "Teenused", url: "/services"},
        {text: "Teenuse e-mailid", url: `/services/${id}/emails`},
    ];

    if (language.length > 0) {
        // breadcrumbLinks.push(
        //     {text: language.toUpperCase(), url: `/services/${id}/emails`},
        // )
    }

    const languages = ['est', 'eng'];

    const getEmailText = (emailType: string, targetType: keyof EmailText): string => {
        const emailText = service?.email_texts.find(text => text.email_type === emailType && text.language === language);
        return emailText ? String(emailText[targetType as keyof EmailText]) : '';
    };

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            client_email_title: getEmailText('client_email', 'title'),
            client_email_content: getEmailText('client_email', 'content'),
            client_loading_content: getEmailText('client_loading', 'content'),
            client_unloading_content: getEmailText('client_unloading', 'content'),
            vendor_email_title: getEmailText('vendor_email', 'title'),
            vendor_email_content: getEmailText('vendor_email', 'content'),
            vendor_loading_content: getEmailText('vendor_loading', 'content'),
            vendor_unloading_content: getEmailText('vendor_unloading', 'content'),
            client_contact_email_title: getEmailText('client_contact_email', 'title'),
            client_contact_email_content: getEmailText('client_contact_email', 'content'),
            vendor_contact_email_title: getEmailText('vendor_contact_email', 'title'),
            vendor_contact_email_content: getEmailText('vendor_contact_email', 'content'),
        },
        onSubmit: (values: any) => {
            values.language = language;

            dispatch(updateEmails({id, data: values}));
        },
        enableReinitialize: true
    });

    if (!service) {
        return null;
    }

    const {values, setFieldValue, handleSubmit} = formik;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Emailide tekstid uuendatud</h2>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed && servicesState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {servicesState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            <h1>{service.name} emailid</h1>
            {language.length < 1 &&
            <div className="form">
                {languages.map(language => (
                    <Fragment key={language}>
                        <a href="Javascript:;" onClick={() => setLanguage(language)}>{language.toUpperCase()}</a><br/>
                    </Fragment>
                ))}
            </div>
            }
            {language.length > 0 &&
            <FormikProvider value={formik}>
              <Form onSubmit={handleSubmit}>
                <div className="form">
                  <h2><span>Teenuse andmed</span></h2>
                  <table className="form">
                    <tbody>
                    {service?.client_email && (
                        <>
                            <WysiwygEditorField
                                label="Kliendi kirja pealkiri"
                                name="client_email_title"
                            />
                            <WysiwygEditorField
                                label="Kliendi kiri"
                                name="client_email_content"
                                showSmall={true}
                            />
                            <WysiwygEditorField
                                label="Kliendi laadimiskoht"
                                name="client_loading_content"
                                showSmall={true}
                            />
                            <WysiwygEditorField
                                label="Kliendi mahalaadimiskoht"
                                name="client_unloading_content"
                                showSmall={true}
                            />
                        </>
                    ) || null}
                    {service?.vendor_email && (
                        <>
                            <WysiwygEditorField
                                label="Hankija kirja pealkiri"
                                name="vendor_email_title"
                            />
                            <WysiwygEditorField
                                label="Hankija kiri"
                                name="vendor_email_content"
                                showSmall={true}
                            />
                            <WysiwygEditorField
                                label="Hankija laadimiskoht"
                                name="vendor_loading_content"
                                showSmall={true}
                            />
                            <WysiwygEditorField
                                label="Hankija mahalaadimiskoht"
                                name="vendor_unloading_content"
                                showSmall={true}
                            />
                        </>
                    ) || null}
                    {service?.client_contact_email && (
                        <>
                            <WysiwygEditorField
                                label="Pealelaadija kontakti kirja pealkiri"
                                name="client_contact_email_title"
                            />
                            <WysiwygEditorField
                                label="Pealelaadija kontakti kiri:"
                                name="client_contact_email_content"
                                showSmall={true}
                            />
                        </>
                    ) || null}
                    {service?.vendor_contact_email && (
                        <>
                            <WysiwygEditorField
                                label="Mahalaadija kontakti kirja pealkiri"
                                name="vendor_contact_email_title"
                            />
                            <WysiwygEditorField
                                label="Mahalaadija kontakti kiri"
                                name="vendor_contact_email_content"
                                showSmall={true}
                            />
                        </>
                    ) || null}
                    </tbody>
                  </table>
                </div>
                  {requestStatus === RequestStatus.Success && (
                      <div className="message msg-ok">
                          <div>
                              <h2>Emailide tekstid uuendatud</h2>
                          </div>
                      </div>
                  )}
                  {requestStatus === RequestStatus.Failed && servicesState.errors.length > 0 && (
                      <div className="message msg-error">
                          <div>
                              <h2>Viga:</h2>
                              {servicesState.errors.map((error, index) => (
                                  <span key={index}>{error}<br/></span>
                              ))}
                          </div>
                      </div>
                  )}
                <div className="action clear">
                  <p>
                    <button type="submit">
                      <span><strong>Muuda</strong></span></button>
                    <a href="Javascript:;" onClick={() => setLanguage('')}>Tühista</a>
                  </p>
                </div>
              </Form>
            </FormikProvider>
            }
        </LoggedInView>
    )

}